/**
 * Shortcut Store
 *
 * Zustand store for tracking keyboard shortcut state.
 *
 * State:
 *   - `activeScope`: Which shortcut scope is currently active
 *   - `overlayOpen`: Whether the shortcuts overlay (Cmd+/) is open
 *   - `scopeStack`: Stack of scopes for nested contexts (e.g., dialog over card)
 *   - `chordPrefix` / `chordPrefixTimestamp`: Two-key chord state machine for
 *     `g <letter>` global navigation. When `chordPrefix` is non-null the next
 *     keypress is interpreted as the chord follower; auto-clears after the
 *     1000ms TTL.
 */

import { create } from "zustand";
import type { ShortcutScope } from "@/shared/lib/shortcuts";
import { registerChordPrefixProvider } from "@/shared/lib/shortcuts";

export interface ShortcutState {
  /** The currently active shortcut scope. */
  activeScope: ShortcutScope;
  /** Stack of scopes for proper nesting (modals push onto stack). */
  scopeStack: ShortcutScope[];
  /** Whether the shortcuts overlay is open. */
  overlayOpen: boolean;
  /** The chord prefix key if a chord sequence is in progress (e.g., "g"). */
  chordPrefix: string | null;
  /** Timestamp when the chord prefix was pressed (for TTL). */
  chordPrefixTimestamp: number;

  /** Set the active scope directly. */
  setActiveScope: (scope: ShortcutScope) => void;
  /** Push a scope onto the stack (for modals/dialogs). */
  pushScope: (scope: ShortcutScope) => void;
  /** Pop the top scope from the stack. */
  popScope: () => void;
  /** Set the overlay open state. */
  setOverlayOpen: (open: boolean) => void;
  /** Toggle the overlay. */
  toggleOverlay: () => void;
  /** Set the chord prefix. Auto-clears after 1000ms via the chord state machine. */
  setChordPrefix: (prefix: string | null) => void;
  /** Clear the chord prefix. */
  clearChordPrefix: () => void;
}

export const useShortcutStore = create<ShortcutState>((set) => ({
  activeScope: "global",
  scopeStack: [],
  overlayOpen: false,
  chordPrefix: null,
  chordPrefixTimestamp: 0,

  setActiveScope: (scope) => set({ activeScope: scope }),

  pushScope: (scope) =>
    set((state) => ({
      scopeStack: [...state.scopeStack, state.activeScope],
      activeScope: scope,
    })),

  popScope: () =>
    set((state) => {
      if (state.scopeStack.length === 0) {
        return { activeScope: "global" };
      }
      const newStack = [...state.scopeStack];
      const prevScope = newStack.pop() ?? "global";
      return {
        scopeStack: newStack,
        activeScope: prevScope,
      };
    }),

  setOverlayOpen: (open) => set({ overlayOpen: open }),

  toggleOverlay: () => set((state) => ({ overlayOpen: !state.overlayOpen })),

  setChordPrefix: (prefix) =>
    set({ chordPrefix: prefix, chordPrefixTimestamp: Date.now() }),

  clearChordPrefix: () => set({ chordPrefix: null, chordPrefixTimestamp: 0 }),
}));

// Register the chord-prefix provider with the shortcuts library so
// `matchesShortcut` can read the active chord prefix without forcing every
// per-page handler to thread it through manually. See
// `shared/lib/shortcuts.ts` `registerChordPrefixProvider`.
registerChordPrefixProvider(() => useShortcutStore.getState().chordPrefix);
